import React, { useState, useEffect, useRef } from 'react';
import { FaPen, FaClock, FaCheck, FaTimesCircle, FaUndoAlt, FaBan, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import Portal from './portal';

const StatusDropdown = ({ submissionId, currentStatus, onStatusChange }) => {
    const [status, setStatus] = useState(currentStatus);
    const [isOpen, setIsOpen] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [newStatus, setNewStatus] = useState(null);
    const [reason, setReason] = useState("");
    const [customReason, setCustomReason] = useState("");
    const [isCustomReason, setIsCustomReason] = useState(false);
    const dropdownRef = useRef(null);

    const statuses = ['paid', 'in-progress', 'completed', 'rejected', 'refunded', 'cancelled'];
    const statusTranslations = {
        'paid': 'Payée',
        'in-progress': 'En cours',
        'completed': 'Complété',
        'rejected': 'Rejeté',
        'refunded': 'Remboursé',
        'cancelled': 'Annulé'
    };

    const statusIcons = {
        'paid': <FaPen />,
        'in-progress': <FaClock />,
        'completed': <FaCheck />,
        'rejected': <FaTimesCircle />,
        'refunded': <FaUndoAlt />,
        'cancelled': <FaBan />
    };

    const rejectionReasons = [
        "Pièce d'identité non conforme : la mention obligatoire est manquante",
        "Pièce d'identité non conforme : la pièce d'identité est manquante",
        "Pièce d'identité non conforme : la pièce d'identité est impossible à ouvrir",
        "Pièce d'identité non conforme : la mention obligatoire est manquante et la pièce d'identité est invalide",
        "Pièce d'identité non conforme : la pièce d'identité est invalide",
        "Pièce d'identité non conforme : la pièce d'identité est non lisible",
        "Pièce d'identité non conforme : la pièce d'identité doit se tenir sur une seule page",
        "Adresse postale incorrecte : merci de nous contacter afin de pouvoir modifier celle-ci et pouvoir procéder à votre enregistrement URSSAF",
        "Pièce d'identité non conforme : la pièce d'identité est coupée et donc non lisible",
        "Pièce d'identité non conforme : la mention obligatoire est manquante et la pièce d'identité est manquante",
        "Pièce d'identité non conforme : La mention obligatoire est non lisible",
        "Pièce d'identité non conforme : la mention obligatoire ne doit pas empiéter sur la pièce d'identité",
        "Merci de nous fournir également votre bon pour pouvoir disponible dans votre bibliothèque de fichier.",
        "Merci de fournir le recto/verso de votre CNI",
        "Pièce d'identité non conforme : le pouvoir est impossible à ouvrir",
        "Pièce d'identité non conforme : le pouvoir est non lisible",
        "Pièce d'identité non conforme : Aucune signature sur la mention complémentaire",
        "Pièce d'identité non conforme : Aucune signature sur le pouvoir",
        "Autre"
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            const isDropdownClick = event.target.closest('.status-dropdown-portal');
            const isButtonClick = dropdownRef.current?.contains(event.target);

            // Don't close if clicked inside the dropdown or button
            if (!isDropdownClick && !isButtonClick) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleStatusChange = (statusOption) => {
        console.log(statusOption);

        setNewStatus(statusOption);
        setShowConfirmation(true);
        if (statusOption !== 'rejected') {
            setIsCustomReason(false);
            setReason("");
            setCustomReason("");
        }
    };

    const confirmStatusChange = async () => {
        setShowConfirmation(false);
        setStatus(newStatus);
        setIsOpen(false);
        if (onStatusChange) {
            onStatusChange(submissionId, newStatus, newStatus === 'rejected' ? (isCustomReason ? customReason : reason) : null);
        }
    };

    return (
        <>
            <div className="relative inline-block text-left w-40" ref={dropdownRef}>
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="p-0.5 inline-flex justify-between rounded-[30px] items-center gap-1 w-full border border-gray-300 bg-white text-sm focus:outline-none"
                >
                    <div className={`status-${status} flex items-center gap-1 rounded-[30px]`}>
                        <span className="status-icon">{statusIcons[status]}</span>
                        {statusTranslations[status]}
                    </div>
                    {isOpen ? (
                        <FaChevronUp className="h-3.5 w-6 text-gray-500" />
                    ) : (
                        <FaChevronDown className="h-3.5 w-6 text-gray-500" />
                    )}
                </button>

                {isOpen && (
                    <Portal>
                        <div className="status-dropdown-portal origin-top-right absolute right-0 mt-1 w-52 rounded shadow bg-white ring-1 ring-black ring-opacity-5 z-[9999]"
                            style={{
                                position: 'absolute',
                                top: dropdownRef.current?.getBoundingClientRect().bottom + window.scrollY,
                                left: dropdownRef.current?.getBoundingClientRect().left,
                            }}>
                            <div>
                                {statuses.filter(statusOption => statusOption !== status).map((statusOption) => (
                                    <button
                                        key={statusOption}
                                        className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                        onClick={() => handleStatusChange(statusOption)}
                                    >
                                        <div className={`status-${statusOption} flex items-center gap-1`}>
                                            <span className="status-icon">{statusIcons[statusOption]}</span>
                                            {statusTranslations[statusOption]}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </Portal>
                )}

                {showConfirmation && newStatus !== 'rejected' && (
                    <Portal>
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-[10000]">
                            <div className="bg-white p-4 rounded-lg shadow-lg w-auto max-w-lg mx-auto">
                                <p className="text-sm">Êtes-vous sûr de vouloir changer le statut à "<strong>{statusTranslations[newStatus]}</strong>" ?</p>
                                <div className="mt-4 flex justify-end gap-2">
                                    <button
                                        onClick={() => setShowConfirmation(false)}
                                        className="px-3 py-1 text-sm bg-gray-200 rounded"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        onClick={confirmStatusChange}
                                        className="px-3 py-1 text-sm bg-primary text-white rounded"
                                    >
                                        Confirmer
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Portal>
                )}
            </div>

            {showConfirmation && newStatus === 'rejected' && (
                <div className="fixed top-0 left-0 right-0 bottom-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-[10000]">
                    <div className="bg-white p-4 rounded-lg w-full max-w-lg shadow-lg">
                        <p className="text-sm mb-2">Veuillez sélectionner une raison pour le rejet :</p>

                        <div className="border rounded max-h-52 overflow-y-auto divide-y">
                            {rejectionReasons.map((r, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => {
                                        setReason(r);
                                        setIsCustomReason(r === 'Autre');
                                    }}
                                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 font-semibold transition-opacity duration-100 ${reason === r ? 'text-black' : 'opacity-40'}`}>
                                    {r}
                                </button>
                            ))}
                        </div>

                        {isCustomReason && (
                            <input
                                type="text"
                                value={customReason}
                                onChange={(e) => setCustomReason(e.target.value)}
                                placeholder="Veuillez spécifier la raison"
                                className="mt-2 p-2 text-sm border rounded w-full"
                            />
                        )}

                        <div className="mt-3 flex justify-end gap-2">
                            <button
                                onClick={() => setShowConfirmation(false)}
                                className="px-3 py-1 text-sm bg-gray-200 rounded"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={confirmStatusChange}
                                className="px-3 py-1 text-sm bg-primary text-white rounded"
                                disabled={!reason || (isCustomReason && !customReason)}
                            >
                                Confirmer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StatusDropdown;